import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LocalDate } from 'src/utilities/local-date.js';

import '../../library/elements/d-action-toggle.js';
import '../../library/elements/d-section.js';
import '../../library/fields/d-expansion.js';
import '../../library/lists/d-timekeeping-list-section.js';
import type { EmployeeTimekeeping } from './employee-timekeeping';
import type { EmployeeGroup } from 'src/pages/staffing-page/employee-group';
import type { TimekeepingBeforeEmploymentStartWarning } from 'src/utilities/time-balance/types';

/**
 *
 */
@customElement('d-timekeeping-review')
export class DTimekeepingReview extends LitElement {
  static readonly styles = [
    css`
      :host {
        display: block;
      }

      .group {
        display: flex;
        gap: 12px;
        border-top: 1px solid var(--borderColorOnGray);
      }

      .color-bar {
        flex: none;
        width: 14px;
        background-color: hsl(196, 83%, 49%);
      }

      .employees {
        flex: 1;
        margin-top: -1px;
        margin-bottom: -1px;
      }

      d-timekeeping-list-section {
        max-width: 800px;
        margin-bottom: 8px;
      }

      .terminated-section {
        opacity: 0.6;
      }

      .terminated-header {
        padding: 12px 0;
        border-top: 1px solid var(--borderColorOnGray);
      }
    `,
  ];
  /**
   * The default date for new entries. Normally today.
   */
  @property({ type: String })
  defaultDate = '';
  @property({ type: Array })
  employeeGroups: EmployeeGroup[] = [];
  @property({ type: Array })
  employeesWithPeriods: EmployeeTimekeeping[] = [];
  @property({ type: Boolean })
  currentUserHasAccess = false;
  @property({ type: Boolean })
  sortByGroup = false;
  @property({ type: Number })
  contentStickyTop = 0;
  @property({ type: Object })
  timekeepingBeforeEmploymentStartWarning?: TimekeepingBeforeEmploymentStartWarning;
  @state()
  private showTerminated = false;

  private isTerminated(employee: EmployeeTimekeeping): boolean {
    const endDate = employee.forStaffing.employmentEndDate;
    return !!endDate && LocalDate.fromString(endDate).isBefore(LocalDate.now());
  }

  private get activeEmployees(): EmployeeTimekeeping[] {
    return this.employeesWithPeriods.filter((e) => !this.isTerminated(e));
  }

  private get terminatedEmployees(): EmployeeTimekeeping[] {
    return this.employeesWithPeriods.filter((e) => this.isTerminated(e));
  }

  private groupStyle(index: number): string {
    if (index === 1) {
      return 'opacity: 0.6';
    }
    if (index === 2) {
      return 'opacity: 0.2';
    }
    return '';
  }

  private get employeesWithPeriodsByGroup(): EmployeeTimekeeping[][] {
    const employeeMap = new Map(this.employeesWithPeriods.map((emp) => [emp.uuid, emp]));
    return this.employeeGroups.map((g) =>
      g.items
        .map((item) => employeeMap.get(item.uuid))
        .filter((emp): emp is EmployeeTimekeeping => emp !== undefined)
        .sort((a, b) => a.name.localeCompare(b.name)),
    );
  }

  renderEmployees(employees: EmployeeTimekeeping[]) {
    return html`
      ${employees.map(
        (employee) =>
          html` <d-timekeeping-list-section
            .employee=${employee.forStaffing}
            .periods=${employee.periods}
            .saldo=${employee.saldo}
            .listHeader=${employee.name}
            .defaultDate=${this.defaultDate}
            .currentUserHasAccess=${this.currentUserHasAccess}
            .timekeepingBeforeEmploymentStartWarning=${this.timekeepingBeforeEmploymentStartWarning}
            review
            .icon=${'employees'}
            .contentStickyTop=${this.contentStickyTop}
          >
          </d-timekeeping-list-section>`,
      )}
    `;
  }

  private renderTerminatedSection() {
    if (this.terminatedEmployees.length === 0) {
      return nothing;
    }
    return html`
      <div class="terminated-header">
        <d-action-toggle
          .value=${this.showTerminated}
          onLabel="Skjul sluttede (${this.terminatedEmployees.length})"
          offLabel="Vis sluttede (${this.terminatedEmployees.length})"
          @value-changed=${(e: CustomEvent<{ value: boolean }>) => {
            this.showTerminated = e.detail.value;
          }}
        ></d-action-toggle>
      </div>
      ${this.showTerminated
        ? html`<div class="terminated-section">${this.renderEmployees(this.terminatedEmployees)}</div>`
        : nothing}
    `;
  }

  render() {
    if (this.sortByGroup) {
      return html`
        ${this.employeesWithPeriodsByGroup.map(
          (group, index) =>
            html` <div class="group">
              <div class="color-bar" style=${this.groupStyle(index)}></div>
              <div class="employees">${this.renderEmployees(group.filter((e) => !this.isTerminated(e)))}</div>
            </div>`,
        )}
        ${this.renderTerminatedSection()}
      `;
    }
    return html` ${this.renderEmployees(this.activeEmployees)} ${this.renderTerminatedSection()} `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-timekeeping-review': DTimekeepingReview;
  }
}
